"use client";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { authApi, LoginResponse } from "@/api/auth";
import { usersApi } from "@/api/users";
import { LoginCredentialsType, UserType } from "@/types/AuthTypes";
import { rolesApi } from "@/api/roles";
import { PermissionType } from "@/types/PermissionTypes";
import { logger } from "@/utils/logger";
import { useLimitsStore } from "@/store/limitsStore";

/** localStorage key holding the admin's own token while an impersonation is active. */
const IMPERSONATION_ORIGIN_TOKEN_KEY = "impersonation_origin_token";

interface AuthContextType {
  user: UserType | null;
  token: string | null;
  isAppLoading: boolean;
  login: (credentials: LoginCredentialsType) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  isLTIUser: boolean;
  /** True only when the current session was started via loginWithLTIToken.
   *  False for username/password logins even if the user account has an lti_user_id. */
  isLTISession: boolean;
  loginWithLTIToken: (ltiToken: string) => Promise<void>;
  /** After skip-login exchange: persist JWT then load user via getMe (same idea as post-login), plus permissions. */
  loginWithSkipLoginExchange: (token: string) => Promise<void>;
  verifyMfa: (code: string, portalLoginId: number) => Promise<void>;
  allPermissions: PermissionType[];
  markPrivacyPolicyAccepted: () => void;
  /**
   * Reload the signed-in user from /me. The user — with their clients' and groups' settings — is
   * otherwise fetched once per page load, so a setting saved since then stays invisible until a
   * reload. Pages whose behaviour depends on those settings call this when they open.
   */
  refreshUser: () => Promise<void>;
  /** True when the current session is impersonating another user. */
  isImpersonating: boolean;
  /** The real admin behind an impersonation (from /me), or null. */
  impersonationInfo: { impersonator_id: number; impersonator_name: string | null } | null;
  /** Start impersonating the given user (swaps the active token). */
  impersonate: (targetUserId: number) => Promise<void>;
  /** Stop impersonating and restore the admin's own session. */
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isLTIUser, setIsLTIUser] = useState(false);
  const [isLTISession, setIsLTISession] = useState(false);
  const [allPermissions, setAllPermissions] = useState<PermissionType[]>([]);
  /**
   * Whether the permission catalogue has been fetched — success OR failure,
   * because what matters is that we are no longer waiting on it.
   *
   * The app must not call itself loaded before this. isPermitted needs the
   * catalogue to tell whether a permission is skip_has_all_permissions; with
   * an empty list that lookup finds nothing and the has_all_permissions branch
   * falls through to "granted". So for the moment between the user arriving
   * and the catalogue arriving, every permission reads as allowed, and screens
   * render things the person is not entitled to and then take them away again.
   * That is the flash of widgets appearing and vanishing on the dashboard.
   */
  const [arePermissionsResolved, setArePermissionsResolved] = useState(false);

  useEffect(() => {
    // Check for stored token and user data on mount
    const storedToken = localStorage.getItem("token");
    // Restore LTI session flag — true only if the session was originally started via LTI token
    const storedLTISession = localStorage.getItem("lti_session") === "true";
    setIsLTISession(storedLTISession);

    if (storedToken) {
      setToken(storedToken);
    } else {
      setIsAppLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await authApi.getMe();
          if (
            response.data?.id != undefined &&
            user?.id !== response.data?.id
          ) {
            setUser(response.data);
            setIsLTIUser(!!response.data.lti_user_id);

            // Load all permissions after user is authenticated
            try {
              const permissionsResponse = await rolesApi.getAllPermissions();
              if (permissionsResponse.data) {
                setAllPermissions(permissionsResponse.data);
              }
            } catch (error) {
              logger.error("Failed to load permissions:", error);
            } finally {
              // Resolved either way: on failure the app must still start,
              // just with an empty catalogue, rather than hang on a spinner.
              setArePermissionsResolved(true);
            }

            useLimitsStore.getState().fetchLimits();
          }
        } catch (error) {
          setIsAppLoading(false);
          // 401 = invalid/expired token; axios interceptor already dispatches auth:logout
          if (!axios.isAxiosError(error) || error.response?.status !== 401) {
            logger.error("Error fetching user:", error);
          }
        }
      }
    };
    fetchUser();
  }, [token, user]);

  useEffect(() => {
    // Both, not just the user: permission checks are wrong until the
    // catalogue is in, and everything downstream keys off isAppLoading.
    if (user && arePermissionsResolved) {
      setIsAppLoading(false);
    }
  }, [user, arePermissionsResolved]);

  const loginWithLTIToken = useCallback(async (ltiToken: string) => {
    try {
      setIsAppLoading(true);
      // Store LTI token temporarily
      localStorage.setItem("lti_token", ltiToken);

      // Call your backend to validate LTI token and get user info
      const response = await authApi.validateLTIToken(ltiToken);
      setToken(response.data.token);
      setUser(response.data.user);
      setIsLTIUser(true);
      setIsLTISession(true);
      // This path does not fetch the catalogue; the getMe effect will. Marking
      // it resolved here would reintroduce the very window this guards.
      setArePermissionsResolved(false);

      // Store in localStorage
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("lti_session", "true");
    } catch (error) {
      // Only on failure. On success the getMe effect still has to fetch the
      // permission catalogue, and clearing the loading flag here would let the
      // app render before it arrives — the same flash this guards against.
      setIsAppLoading(false);
      throw error;
    } finally {
      localStorage.removeItem("lti_token");
    }
  }, []);

  const loginWithSkipLoginExchange = useCallback(async (tokenValue: string) => {
    try {
      setIsAppLoading(true);
      setToken(tokenValue);
      setIsLTIUser(false);
      setIsLTISession(false);
      localStorage.setItem("token", tokenValue);
      localStorage.removeItem("lti_session");
      const me = await authApi.getMe();
      if (me.data?.id == undefined) {
        throw new Error("Could not load user after skip-login");
      }
      setUser(me.data);
      setIsLTIUser(!!me.data.lti_user_id);
      try {
        const permissionsResponse = await rolesApi.getAllPermissions();
        if (permissionsResponse.data) {
          setAllPermissions(permissionsResponse.data);
        }
      } catch (error) {
        logger.error("Failed to load permissions after skip-login:", error);
      } finally {
        setArePermissionsResolved(true);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsAppLoading(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsLTIUser(false);
    setIsLTISession(false);
    setAllPermissions([]);
    setArePermissionsResolved(false);
    useLimitsStore.getState().clearLimits();

    // Store in localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("lti_token");
    localStorage.removeItem("lti_session");
    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY);
  }, []); // No dependencies since we're only using setState functions which are stable

  useEffect(() => {
    const handleTokenRefresh = (event: CustomEvent) => {
      const { token: newToken } = event.detail;
      setToken(newToken);

      // Store in localStorage
      localStorage.setItem("token", newToken);
    };

    window.addEventListener("auth:logout", handleLogout);
    window.addEventListener(
      "auth:token-refreshed",
      handleTokenRefresh as EventListener,
    );

    return () => {
      window.removeEventListener("auth:logout", handleLogout);
      window.removeEventListener(
        "auth:token-refreshed",
        handleTokenRefresh as EventListener,
      );
    };
  }, [handleLogout]);

  const login = async (
    credentials: LoginCredentialsType,
  ): Promise<LoginResponse> => {
    try {
      const response = await authApi.login(credentials);

      // Username/password login is never an LTI session
      setIsLTISession(false);
      localStorage.removeItem("lti_session");

      // Only set token if MFA is not required
      if (response.token && !response.requiresMfa) {
        setToken(response.token);
        localStorage.setItem("token", response.token);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const verifyMfa = async (code: string, portalLoginId: number) => {
    try {
      const response = await authApi.verifyMfa({
        code,
        portal_login_id: portalLoginId,
      });
      setToken(response.token);
      localStorage.setItem("token", response.token);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      throw error;
    } finally {
      handleLogout();
    }
  };

  const markPrivacyPolicyAccepted = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      return { ...prev, pp_accepted: true };
    });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    const response = await authApi.getMe();
    if (response.data?.id != undefined) setUser(response.data);
  }, [token]);

  const impersonate = useCallback(async (targetUserId: number) => {
    // Preserve the current (admin) token so it can be restored on stop.
    const currentToken = localStorage.getItem("token");
    const response = await usersApi.impersonate(targetUserId);
    const impersonationToken = response.data?.token;
    if (!impersonationToken) {
      throw new Error("No impersonation token returned");
    }
    if (currentToken) {
      localStorage.setItem(IMPERSONATION_ORIGIN_TOKEN_KEY, currentToken);
    }
    setIsAppLoading(true);
    // Swapping the token re-runs the getMe effect, which loads the impersonated
    // user + their permissions/limits — the whole app now reflects that user.
    localStorage.setItem("token", impersonationToken);
    setToken(impersonationToken);
  }, []);

  const stopImpersonation = useCallback(async () => {
    // Revoke + audit server-side (best effort — always restore locally after).
    try {
      await authApi.stopImpersonation();
    } catch (error) {
      logger.error("Failed to stop impersonation on server:", error);
    }

    const originToken = localStorage.getItem(IMPERSONATION_ORIGIN_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY);

    if (!originToken) {
      // Nothing to restore — fall back to a clean logout.
      handleLogout();
      return;
    }

    setIsAppLoading(true);
    localStorage.setItem("token", originToken);
    // The admin's own token may have expired during a long impersonation; refresh
    // it (the endpoint accepts expired tokens and re-checks the still-active admin
    // session) so the admin lands in a fresh session instead of being logged out.
    try {
      const refreshed = await authApi.refreshToken();
      if (refreshed.token) {
        localStorage.setItem("token", refreshed.token);
        setToken(refreshed.token);
        return;
      }
    } catch (error) {
      logger.error("Failed to refresh admin token after stopping impersonation:", error);
    }
    setToken(originToken);
  }, [handleLogout]);

  const impersonationInfo = user?.impersonation ?? null;
  const isImpersonating = impersonationInfo !== null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAppLoading,
        login,
        logout,
        isLTIUser,
        isLTISession,
        loginWithLTIToken,
        loginWithSkipLoginExchange,
        verifyMfa,
        allPermissions,
        markPrivacyPolicyAccepted,
        refreshUser,
        isImpersonating,
        impersonationInfo,
        impersonate,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
